import { useEffect, useEffectEvent, useRef, useState } from 'react';
import {
  useSaveDemoBrand,
  useDeleteDemoBrand,
  useGetDemoBrandColours,
  useInvalidateUseGetDemoBrandColours,
  type DemoBrandResponse,
  type DemoBrandsResponse,
} from '@urbanx/agx.agencies.client';
import { AgxFormTextField, AgxFormDropdown, type AgxNativeFieldProps } from '@urbanx/agx.forms';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  Icon_Arrow,
  useDebouncedState,
  useToast,
} from '@urbanx/agx.ui-components2';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { demoBrandPreviewChanged } from 'features/agencies/agenciesReducer';
import { DemoBrandPreview } from './DemoBrandPreview';
import {
  DemoBrandForm,
  brandingSchema,
  colourSchema,
  demoBrandDefaults,
} from './demoBrandFormDefinition';
import * as styles from './DemoBrands.css';

function ColourInput({ value, onChange, ...props }: AgxNativeFieldProps<string>) {
  return (
    <AgxFormTextField
      {...props}
      value={value}
      onChange={onChange}
      type="text"
      stretch
      leftIcon={
        <span className={styles.colourPicker}>
          <input
            className={styles.colourSwatch}
            aria-labelledby={props.ariaLabelledBy}
            type="color"
            value={colourSchema.safeParse(value).success ? value : '#000000'}
            disabled={props.disabled}
            onChange={(event) => onChange(event.target.value)}
          />
        </span>
      }
    />
  );
}

interface DemoBrandEditorProps {
  data: DemoBrandsResponse;
  preset?: DemoBrandResponse;
  onBack: () => void;
  onSaved: (preset: DemoBrandResponse) => void;
  onDeleted: (data: DemoBrandsResponse) => void;
}

export function DemoBrandEditor({
  data,
  preset,
  onBack,
  onSaved,
  onDeleted,
}: DemoBrandEditorProps) {
  const [id] = useState(() => preset?.id ?? crypto.randomUUID());
  const [readingLogo, setReadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const logoInput = useRef<HTMLInputElement>(null);
  const saveAttempt = useRef<{ fingerprint: string; id: string } | undefined>(undefined);
  const dispatch = useAppDispatch();
  const { pushToast } = useToast();
  const { saveDemoBrand, saveDemoBrandPending } = useSaveDemoBrand();
  const { deleteDemoBrand, deleteDemoBrandPending } = useDeleteDemoBrand();
  const form = DemoBrandForm.useForm({
    defaultValues: demoBrandDefaults(
      preset?.agencyName ?? data.agencyName,
      preset?.branding ?? data.branding,
      preset?.label
    ),
    disableWhileSubmitting: true,
    onSubmit: async ({ parsedValues }) => {
      // Reuse the operation ID for an unchanged retry, including an unknown HTTP outcome.
      const fingerprint = JSON.stringify(parsedValues);
      if (saveAttempt.current?.fingerprint !== fingerprint)
        saveAttempt.current = { fingerprint, id: crypto.randomUUID() };
      try {
        const saved = await saveDemoBrand({
          ...parsedValues,
          id,
          eTag: preset?.eTag,
          saveId: saveAttempt.current.id,
        });
        if (!saved) throw new Error('The preset was not returned after saving.');
        pushToast({ type: 'success', message: 'Brand settings saved.' });
        onSaved(saved);
        return {
          status: 'accepted',
          baseline: demoBrandDefaults(saved.agencyName, saved.branding, saved.label),
        };
      } catch (error) {
        pushToast({
          type: 'error',
          error,
          fallbackMessage: 'Could not save the demo brand.',
        });
        return { status: 'failed', error };
      }
    },
  });
  const busy = readingLogo || saveDemoBrandPending || deleteDemoBrandPending;
  const logoUrl = form.values.logoContent
    ? `data:image/svg+xml;base64,${form.values.logoContent}`
    : form.values.branding.logo?.publicPath;

  const { mainColour, accentColour } = form.values.branding.brandingColours;
  const savedColours = preset?.branding.brandingColours;
  const reuseSavedColours =
    mainColour.toLowerCase() === savedColours?.mainColour?.toLowerCase() &&
    accentColour.toLowerCase() === savedColours?.accentColour?.toLowerCase() &&
    Boolean(
      savedColours?.generatedMainColours?.length && savedColours?.generatedAccentColours?.length
    );
  const [previewInput, setPreviewInput] = useDebouncedState({ mainColour, accentColour });
  const inputIsCurrent =
    previewInput.mainColour === mainColour && previewInput.accentColour === accentColour;
  const { data: generatedColours, isError: colourPreviewError } = useGetDemoBrandColours(
    previewInput.mainColour,
    previewInput.accentColour,
    !reuseSavedColours &&
      inputIsCurrent &&
      colourSchema.safeParse(mainColour).success &&
      colourSchema.safeParse(accentColour).success
  );
  const invalidateColours = useInvalidateUseGetDemoBrandColours();
  const previewColours = reuseSavedColours
    ? savedColours
    : inputIsCurrent
      ? generatedColours
      : undefined;

  const synchronizePreview = useEffectEvent(() => {
    if (!previewColours)
      return form.addBlocker({
        id: 'colour-preview',
        message: 'Wait for the colour preview to load.',
      });
    const parsed = brandingSchema.safeParse(form.values.branding);
    if (!parsed.success) return;
    dispatch(
      demoBrandPreviewChanged({
        branding: {
          ...parsed.data,
          agencyName: form.values.agencyName,
          brandingColours: previewColours,
        },
        logoUrl,
      })
    );
  });
  useEffect(() => synchronizePreview(), [form.values, logoUrl, previewColours]);

  const uploadLogo = async (file: File, onChange: (content: string) => void) => {
    if (!file.name.toLowerCase().endsWith('.svg') || file.size > 2 * 1024 * 1024) {
      setUploadError('Choose an SVG logo under 2MB.');
      return;
    }
    const unblock = form.addBlocker({
      id: 'logo-upload',
      message: 'Wait for the logo to finish loading.',
    });
    setReadingLogo(true);
    setUploadError('');
    try {
      const content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1]);
        reader.onerror = () => reject(new Error('Could not read the logo.'));
        reader.readAsDataURL(file);
      });
      onChange(content);
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Could not read the logo.');
    } finally {
      unblock();
      setReadingLogo(false);
    }
  };

  return (
    <DemoBrandForm.Root
      form={form}
      className={styles.form}
      onChange={() => setPreviewInput(form.form.state.values.branding.brandingColours)}
    >
      <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
        <AgxButton
          id="demo-brands-back"
          text=""
          leftIcon={<Icon_Arrow direction="left" width={24} height={24} color="currentColor" />}
          ariaLabel="Back to selection"
          variant="naked"
          size="small"
          disabled={busy}
          onClick={onBack}
        />
        <AgxTypography
          id="demo-brand-mode-heading"
          type="body_ui_strong"
          size="small"
          role="heading"
          aria-level={3}
        >
          {preset ? 'Edit Brand Override' : 'Create Brand Override'}
        </AgxTypography>
      </AgxSpacer>
      <DemoBrandForm.Field
        name="label"
        Input={AgxFormTextField}
        type="text"
        label="Brand override name"
        hideOptionalText
        stretch
        maxLength={120}
        disabled={busy}
      />
      <div className={styles.agencySection}>
        <AgxSpacer gap="spacing-xl" style={{ minWidth: 0 }}>
          <DemoBrandForm.Field
            name="agencyName"
            Input={AgxFormTextField}
            type="text"
            label="Agency Name"
            hideOptionalText
            stretch
            disabled={busy}
          />
          <DemoBrandForm.CustomField name="logoContent" focus={() => logoInput.current?.focus()}>
            {(field) => (
              <AgxSpacer gap="spacing-md">
                <AgxTypography
                  id="demo-brand-logo-label"
                  type="label"
                  size="medium"
                  htmlFor="demo-brand-logo-input"
                >
                  Agency logo
                </AgxTypography>
                <AgxSpacer direction="horizontal" gap="spacing-md" contentAlignment="center" wrap>
                  {logoUrl && (
                    <img
                      className={styles.logo}
                      src={logoUrl}
                      alt={`${form.values.agencyName} logo`}
                      style={{ background: form.values.branding.backgroundColor }}
                    />
                  )}
                  <input
                    ref={logoInput}
                    className={styles.logoInput}
                    id="demo-brand-logo-input"
                    type="file"
                    accept=".svg"
                    disabled={busy || field.disabled}
                    required={field.required && !logoUrl}
                    aria-invalid={field.hasError}
                    aria-describedby={
                      uploadError || field.errorText ? 'demo-brand-logo-error' : undefined
                    }
                    onBlur={field.onBlur}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void uploadLogo(file, field.onChange);
                      event.target.value = '';
                    }}
                  />
                </AgxSpacer>
                {(uploadError || field.errorText) && (
                  <AgxTypography
                    id="demo-brand-logo-error"
                    type="form_error"
                    size="small"
                    role="alert"
                  >
                    {uploadError || field.errorText}
                  </AgxTypography>
                )}
              </AgxSpacer>
            )}
          </DemoBrandForm.CustomField>
        </AgxSpacer>
        <AgxSpacer gap="spacing-sm" style={{ minWidth: 0 }}>
          <AgxTypography id="demo-brand-preview-label" type="body_ui" size="small">
            Preview
          </AgxTypography>
          <DemoBrandPreview branding={form.values.branding} />
        </AgxSpacer>
      </div>
      <div className={styles.pair}>
        <DemoBrandForm.Field
          name="branding.backgroundColor"
          Input={ColourInput}
          label="Background"
          disabled={busy}
        />
        <DemoBrandForm.Field
          name="branding.backgroundTextColor"
          Input={ColourInput}
          label="BG Text"
          disabled={busy}
        />
      </div>
      <div className={styles.pair}>
        <DemoBrandForm.Field
          name="branding.accentColorOld"
          Input={ColourInput}
          label="Accent"
          disabled={busy}
        />
        <DemoBrandForm.Field
          name="branding.buttonTextColor"
          Input={ColourInput}
          label="Button Text"
          disabled={busy}
        />
      </div>
      <div className={styles.pair}>
        <DemoBrandForm.Field
          name="branding.brandingColours.mainColour"
          Input={ColourInput}
          label="Main"
          disabled={busy}
        />
        <DemoBrandForm.Field
          name="branding.brandingColours.accentColour"
          Input={ColourInput}
          label="Accent V2"
          disabled={busy}
        />
      </div>
      {!previewColours &&
        colourSchema.safeParse(mainColour).success &&
        colourSchema.safeParse(accentColour).success && (
          <div role="status">
            {colourPreviewError && inputIsCurrent ? (
              <>
                Could not load the colour preview.
                <AgxButton
                  id="demo-brands-retry-colours"
                  text="Retry"
                  variant="link"
                  size="small"
                  onClick={() => void invalidateColours(mainColour, accentColour)}
                />
              </>
            ) : (
              'Updating colour preview…'
            )}
          </div>
        )}
      {(['inbox', 'sidebar'] as const).map((area) => (
        <div key={area} className={styles.pair}>
          <DemoBrandForm.Field
            name={`branding.${area}BackgroundColor`}
            Input={AgxFormDropdown}
            label={`${area === 'inbox' ? 'Inbox' : 'Sidebar'} BG`}
            options={[
              { value: '#FFFFFF', label: 'White' },
              { value: '#000000', label: 'Black' },
            ]}
            disabled={busy}
          />
          <DemoBrandForm.Field
            name={`branding.${area}BackgroundColorOpacity`}
            Input={AgxFormTextField}
            type="percent"
            noDecimalPoints
            label={`${area === 'inbox' ? 'Inbox' : 'Sidebar'} Opacity`}
            stretch
            disabled={busy}
          />
        </div>
      ))}
      <AgxSpacer
        direction="horizontal"
        gap="spacing-sm"
        contentJustification="space-between"
        contentAlignment="center"
        wrap
      >
        <DemoBrandForm.Submit>
          {({ submit, canSubmit, isDirty }) => (
            <AgxButton
              id="demo-brands-save"
              text="Save Brand Override"
              variant="primary"
              size="medium"
              disabled={busy || !previewColours || !canSubmit || (!isDirty && Boolean(preset))}
              onClick={() => void submit()}
            />
          )}
        </DemoBrandForm.Submit>
        {preset && (
          <AgxButton
            id="demo-brands-delete"
            text="Delete Override"
            variant="link"
            danger
            size="medium"
            disabled={busy}
            onClick={async () => {
              if (!preset || busy) return;
              const unblock = form.addBlocker({
                id: 'delete-preset',
                message: 'Wait for deletion to finish.',
              });
              try {
                const updated = await deleteDemoBrand({ id: preset.id, eTag: preset.eTag });
                if (!updated)
                  throw new Error('The brand overrides were not returned after deleting.');
                pushToast({ type: 'success', message: 'Brand override deleted.' });
                onDeleted(updated);
              } catch (error) {
                pushToast({
                  type: 'error',
                  error,
                  fallbackMessage: 'Could not delete the brand override.',
                });
              } finally {
                unblock();
              }
            }}
          />
        )}
      </AgxSpacer>
      <button type="submit" hidden disabled={busy} />
    </DemoBrandForm.Root>
  );
}
